import { create } from 'zustand'
import { getAuditLogs, getEventCodes, getDistinctActions, getRecentLogs, exportLogsToCSV } from '@api/auditLogs'

/**
 * Audit Log Store
 *
 * Manages state for audit logs viewing and filtering
 */
export const useAuditLogStore = create((set, get) => ({
	// State
	logs: [],
	eventCodes: {},
	distinctActions: [],
	loading: false,
	error: null,

	// Filters
	filters: {
		user_id: '',
		company_id: '',
		action: '',
		date_from: '',
		date_to: '',
		search: '',
	},

	// Pagination
	pagination: {
		page: 1,
		per_page: 50,
		total: 0,
		total_pages: 0,
	},

	/**
	 * Fetch audit logs with current filters
	 */
	fetchLogs: async () => {
		set({ loading: true, error: null })

		try {
			const { filters, pagination } = get()

			const params = {
				...filters,
				page: pagination.page,
				per_page: pagination.per_page,
			}

			const response = await getAuditLogs(params)

			set({
				logs: response.data || [],
				pagination: {
					...pagination,
					total: response.pagination?.total || 0,
					total_pages: response.pagination?.total_pages || 0,
				},
				loading: false,
			})
		} catch (error) {
			console.error('Failed to fetch audit logs:', error)
			set({
				error: error.message || 'Failed to fetch audit logs',
				loading: false,
			})
		}
	},

	/**
	 * Fetch event codes
	 */
	fetchEventCodes: async () => {
		try {
			const response = await getEventCodes()
			set({ eventCodes: response.data || {} })
		} catch (error) {
			console.error('Failed to fetch event codes:', error)
		}
	},

	/**
	 * Fetch distinct actions
	 */
	fetchDistinctActions: async () => {
		try {
			const response = await getDistinctActions()
			set({ distinctActions: response.data || [] })
		} catch (error) {
			console.error('Failed to fetch distinct actions:', error)
		}
	},

	/**
	 * Set search filter
	 */
	setSearch: (search) => {
		set((state) => ({
			filters: { ...state.filters, search },
			pagination: { ...state.pagination, page: 1 }, // Reset to page 1
		}))
		// Auto-fetch after setting search
		setTimeout(() => get().fetchLogs(), 300)
	},

	/**
	 * Set user filter
	 */
	setUserFilter: (user_id) => {
		set((state) => ({
			filters: { ...state.filters, user_id },
			pagination: { ...state.pagination, page: 1 },
		}))
		get().fetchLogs()
	},

	/**
	 * Set company filter
	 */
	setCompanyFilter: (company_id) => {
		set((state) => ({
			filters: { ...state.filters, company_id },
			pagination: { ...state.pagination, page: 1 },
		}))
		get().fetchLogs()
	},

	/**
	 * Set action filter
	 */
	setActionFilter: (action) => {
		set((state) => ({
			filters: { ...state.filters, action },
			pagination: { ...state.pagination, page: 1 },
		}))
		get().fetchLogs()
	},

	/**
	 * Set date range filter
	 */
	setDateRange: (date_from, date_to) => {
		set((state) => ({
			filters: { ...state.filters, date_from, date_to },
			pagination: { ...state.pagination, page: 1 },
		}))
		get().fetchLogs()
	},

	/**
	 * Clear all filters
	 */
	clearFilters: () => {
		set({
			filters: {
				user_id: '',
				company_id: '',
				action: '',
				date_from: '',
				date_to: '',
				search: '',
			},
			pagination: { ...get().pagination, page: 1 },
		})
		get().fetchLogs()
	},

	/**
	 * Set page
	 */
	setPage: (page) => {
		set((state) => ({
			pagination: { ...state.pagination, page },
		}))
		get().fetchLogs()
	},

	/**
	 * Next page
	 */
	nextPage: () => {
		const { pagination } = get()
		if (pagination.page < pagination.total_pages) {
			get().setPage(pagination.page + 1)
		}
	},

	/**
	 * Previous page
	 */
	prevPage: () => {
		const { pagination } = get()
		if (pagination.page > 1) {
			get().setPage(pagination.page - 1)
		}
	},

	/**
	 * Set items per page
	 */
	setPerPage: (per_page) => {
		set((state) => ({
			pagination: { ...state.pagination, per_page, page: 1 },
		}))
		get().fetchLogs()
	},

	/**
	 * Export current logs to CSV
	 */
	exportLogs: () => {
		const { logs, filters } = get()

		if (logs.length === 0) {
			throw new Error('No logs to export')
		}

		// Generate filename with current filters
		let filename = 'audit-logs'
		if (filters.date_from || filters.date_to) {
			filename += `_${filters.date_from || 'start'}_to_${filters.date_to || 'end'}`
		}
		filename += '.csv'

		exportLogsToCSV(logs, filename)
	},

	/**
	 * Refresh logs (re-fetch with current filters)
	 */
	refresh: () => {
		get().fetchLogs()
	},
}))
